//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

import styled from "@emotion/styled";
import _ from "lodash";
import moment from "moment";
import React, { Component } from "react";
import { RouteComponentProps } from "react-router-dom";
import { Dropdown, Input, Menu, Table } from "semantic-ui-react";
import apiClients from "../../api/apiclients";
import {
  EverestInvoice as ApiEverestInvoice,
  EverestInvoiceList as ApiEverestInvoiceList,
  EverestInvoicePaymentState as ApiEverestInvoicePaymentState,
  EverestInvoiceStatistics as ApiEverestInvoiceStatistics,
  ListAllEverestInvoicesRequest as ApiListEverestInvoicesRequest,
} from "../../api/billing/v1/ibilling";
import {
  EntityDetailsList as ApiEntityList,
  ListAllOrganizationsRequest as ApiListAllOrganizationsRequest,
  Organization as ApiOrganization,
  Tier as ApiTier,
} from "../../api/lib";
import Auth from "../../auth/Auth";
import {
  ContentSegment,
  CreationDateFilter,
  CreationDateFilters,
  ErrorMessage,
  ListActionDownload,
  momentNow,
  PagingButtons,
  Processing,
  SecondaryMenu,
  TextLink,
} from "../../ui/lib";
import { IWithRefreshProps, withRefresh } from "../../util/WithRefresh";
import OrganizationCache, { OrganizationName } from "../../util/OrganizationCache";
import { Routes } from "../../routes";
import { DownloadEverestInvoice } from "./DownloadEverestInvoice";

enum StateFilter {
  All = "All",
  Open = "Open",
  Overdue = "Overdue",
  Unpaid = "Unpaid",
  PartiallyPaid = "PartiallyPaid",
  Paid = "Paid",
  Credited = "Credited",
}

// Label and request fields of every state filter option, in dropdown order.
// Open = not fully settled; Overdue = open with the due day passed (across payment states).
const stateFilters: { value: StateFilter; label: string; request: Partial<ApiListEverestInvoicesRequest> }[] = [
  { value: StateFilter.All, label: "All", request: {} },
  { value: StateFilter.Open, label: "Open only", request: { open_only: true } },
  { value: StateFilter.Overdue, label: "Overdue", request: { overdue_only: true } },
  {
    value: StateFilter.Unpaid,
    label: "Unpaid",
    request: { payment_state: ApiEverestInvoicePaymentState.EVEREST_INVOICE_PAYMENT_STATE_UNPAID },
  },
  {
    value: StateFilter.PartiallyPaid,
    label: "Partially paid",
    request: { payment_state: ApiEverestInvoicePaymentState.EVEREST_INVOICE_PAYMENT_STATE_PARTIALLY_PAID },
  },
  {
    value: StateFilter.Paid,
    label: "Paid",
    request: { payment_state: ApiEverestInvoicePaymentState.EVEREST_INVOICE_PAYMENT_STATE_PAID_IN_FULL },
  },
  {
    value: StateFilter.Credited,
    label: "Credited",
    request: { payment_state: ApiEverestInvoicePaymentState.EVEREST_INVOICE_PAYMENT_STATE_CREDITED_IN_FULL },
  },
];

const stateFilterOf = (v: StateFilter) => stateFilters.find((f) => f.value === v) || stateFilters[0];

// The API returns enum values as strings in JSON, while the generated TS enum is numeric.
const normalizePaymentState = (state?: ApiEverestInvoicePaymentState | string | null): ApiEverestInvoicePaymentState | undefined => {
  if (state == null || state === "") {
    return ApiEverestInvoicePaymentState.EVEREST_INVOICE_PAYMENT_STATE_UNSPECIFIED;
  }
  if (typeof state === "number") {
    return state;
  }
  const stateFromEnumName = ApiEverestInvoicePaymentState[state as keyof typeof ApiEverestInvoicePaymentState];
  if (typeof stateFromEnumName === "number") {
    return stateFromEnumName;
  }
  const numericState = Number(state);
  if (Number.isFinite(numericState)) {
    return numericState;
  }
  return undefined;
};

const stateLabel = (item: ApiEverestInvoice) => {
  if (item.is_overdue) {
    return "Overdue";
  }
  switch (normalizePaymentState(item.payment_state)) {
    case ApiEverestInvoicePaymentState.EVEREST_INVOICE_PAYMENT_STATE_UNPAID:
      return "Unpaid";
    case ApiEverestInvoicePaymentState.EVEREST_INVOICE_PAYMENT_STATE_PARTIALLY_PAID:
      return "Partially paid";
    case ApiEverestInvoicePaymentState.EVEREST_INVOICE_PAYMENT_STATE_PAID_IN_FULL:
      return "Paid";
    case ApiEverestInvoicePaymentState.EVEREST_INVOICE_PAYMENT_STATE_CREDITED_IN_FULL:
      return "Credited";
    default:
      return "Unknown";
  }
};

const formatAmount = (a?: { amount?: string; currency?: string }) => (a && a.amount ? `${a.amount} ${a.currency || ""}` : "-");

// groupThousands adds thousand separators to a decimal string without any float
// math (amounts are money and must not go through parseFloat for display).
const groupThousands = (s?: string): string => {
  if (!s) return "-";
  const neg = s.startsWith("-");
  const body = neg ? s.slice(1) : s;
  const [intPart, decPart] = body.split(".");
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${neg ? "-" : ""}${grouped}${decPart !== undefined ? "." + decPart : ""}`;
};

// amountIsPositive is only used to decide whether to visually stress "amount due"
// (a >0 check tolerates float; it never touches the displayed value).
const amountIsPositive = (a?: { amount?: string }): boolean => !!a && parseFloat(a.amount || "0") > 0;

// StatsCard is the summary panel above the invoice table. Colour is reserved for
// state: overdue = critical, amount-due-owed = warning, settled = good; every other
// figure stays in neutral ink. Light-only to match the surrounding idashboard chrome.
const StatsCard = styled.div`
  --surface: #ffffff;
  --surface-2: #f6f8f6;
  --border: #e5e9e5;
  --ink: #182220;
  --ink-2: #56635d;
  --ink-muted: #8b958f;
  --accent: #3f8f2e;
  --good: #2f8f3f;
  --warn: #b3760a;
  --critical: #c23b32;
  --critical-bg: rgba(194, 59, 50, 0.1);

  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 11px;
  box-shadow: 0 1px 2px rgba(20, 30, 25, 0.04), 0 6px 18px rgba(20, 30, 25, 0.05);
  overflow: hidden;
  margin-bottom: 14px;

  .stats-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 16px;
    border-bottom: 1px solid var(--border);
  }
  .stats-eyebrow {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: var(--ink-muted);
  }
  .stats-context {
    font-size: 12px;
    color: var(--ink-muted);
    text-align: right;
  }
  .stats-context b {
    color: var(--ink-2);
    font-weight: 600;
  }

  /* card split: left takes the slack, right sizes to the amounts, so few
     currencies -> wide left -> counters fit on one row (lower panel height) */
  .split {
    display: grid;
    grid-template-columns: 1fr auto;
  }
  .cell {
    padding: 9px 14px;
    min-width: 0;
  }
  .cell.left {
    border-right: 1px solid var(--border);
  }
  .cell.head {
    border-bottom: 1px solid var(--border);
  }
  .cell.body.right {
    background: var(--surface-2);
  }

  .hrow {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 14px;
  }
  .title {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: var(--ink-muted);
    white-space: nowrap;
  }
  .ctx {
    font-size: 12px;
    color: var(--ink-muted);
    text-align: right;
  }
  .ctx b {
    color: var(--ink-2);
    font-weight: 600;
  }

  /* counters — one row, thin dividers */
  .kpis {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
  }
  .kpi {
    padding: 3px 14px;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .kpi:first-child {
    padding-left: 2px;
  }
  .kpi + .kpi::before {
    content: "";
    position: absolute;
    left: 0;
    top: 4px;
    bottom: 4px;
    width: 1px;
    background: var(--border);
  }
  .kpi__value {
    font-size: 21px;
    line-height: 1;
    font-weight: 650;
    color: var(--ink);
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.01em;
  }
  .kpi__label {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--ink-muted);
    white-space: nowrap;
  }
  .kpi__label .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.9;
    flex: none;
  }
  .kpi--total .kpi__value {
    font-size: 23px;
  }
  .kpi--total .kpi__label {
    color: var(--accent);
  }
  .kpi--good .kpi__value,
  .kpi--good .kpi__label {
    color: var(--good);
  }
  .kpi--overdue .kpi__value,
  .kpi--overdue .kpi__label {
    color: var(--critical);
  }

  /* amounts — currencies side by side, each with its own Total/Due headers */
  .currencies {
    display: flex;
    flex-wrap: wrap;
    gap: 26px;
  }
  .cur {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .cur-code {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.05em;
    color: var(--ink-2);
    padding-bottom: 3px;
    border-bottom: 1px solid var(--border);
  }
  .cur-tbl {
    display: grid;
    grid-template-columns: auto auto;
    column-gap: 18px;
    row-gap: 2px;
    align-items: baseline;
  }
  .cur-tbl .h {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--ink-muted);
    text-align: right;
  }
  .cur-tbl .v {
    font-size: 15px;
    font-weight: 600;
    color: var(--ink);
    font-variant-numeric: tabular-nums;
    text-align: right;
    letter-spacing: -0.01em;
  }
  .cur-tbl .v.due {
    color: var(--warn);
  }
  .cur-tbl .v.due.zero {
    color: var(--ink-muted);
  }

  @media (max-width: 720px) {
    .split {
      grid-template-columns: 1fr;
    }
    .cell.left {
      border-right: none;
    }
    .cell.body.left {
      border-bottom: 1px solid var(--border);
    }
  }
`;

interface IRowView {
  item: ApiEverestInvoice;
  orgCache: OrganizationCache;
  onInvoiceSelected: (id: string) => void;
  onOrganizationSelected: (id: string) => void;
  onDownload: () => void;
}

const RowView = ({ ...args }: IRowView) => {
  const inv = args.item;
  return (
    <Table.Row negative={!!inv.is_overdue}>
      <Table.Cell>
        <TextLink href={Routes.dashboard_sales_everest_invoices_detailsWithId(inv.invoice_uuid || "")}>{inv.invoice_number}</TextLink>
      </Table.Cell>
      <Table.Cell>
        <OrganizationName organizationId={inv.organization_id} cache={args.orgCache} />
        {inv.organization_id ? ` (${inv.organization_id})` : ""}
      </Table.Cell>
      <Table.Cell>{inv.invoice_date ? moment(inv.invoice_date).format("YYYY-MM-DD") : "-"}</Table.Cell>
      <Table.Cell>{inv.due_date ? moment(inv.due_date).format("YYYY-MM-DD") : "-"}</Table.Cell>
      <Table.Cell>{formatAmount(inv.total_amount)}</Table.Cell>
      <Table.Cell>{formatAmount(inv.total_amount_due)}</Table.Cell>
      <Table.Cell>{stateLabel(inv)}</Table.Cell>
      <Table.Cell>
        <ListActionDownload onClick={args.onDownload} />
      </Table.Cell>
    </Table.Row>
  );
};

// FilterMenu is the filter bar's Menu with tighter item padding, so the controls
// sit closer to the bar's frame (lower overall height) without shrinking them.
const FilterMenu = styled(Menu)`
  &.ui.menu .item {
    padding-top: 0.3em;
    padding-bottom: 0.3em;
  }
`;

interface IEverestInvoiceListProps extends IWithRefreshProps, RouteComponentProps {
  auth: Auth;
  organization_id?: string;
  showHeader: boolean;
  // headerTitle overrides the section header text (defaults to "Invoices").
  headerTitle?: string;
  onInvoiceSelected: (id: string) => void;
  onOrganizationSelected: (id: string) => void;
}

interface IOrgOption {
  key: string;
  value: string;
  text: string;
}

interface IEverestInvoiceListState {
  errorMessage?: string;
  invoices?: ApiEverestInvoiceList;
  downloading: boolean;
  stateFilter: StateFilter;
  // Invoice-number search (case-insensitive substring).
  invoiceNumber: string;
  page: number;
  pageSize: number;
  // Selected organization filter (organization id) and its display label.
  orgId: string;
  orgText: string;
  // Typeahead state for the organization name search.
  orgOptions: IOrgOption[];
  orgSearchLoading: boolean;
  // Invoice-date range filter as YYYY-MM-DD strings; "" means the bound is unset.
  invoiceDateFromStr: string;
  invoiceDateToStr: string;
  // Selected quick-range preset id ("all" = no range, "" = custom/manually edited).
  datePresetID: string;
  // Organization tier / legal entity filters ("" = no filter) and their option
  // sources. Both are denormalized onto the snapshots by everestd at sync time.
  tierID: string;
  entityID: string;
  tiers?: ApiTier[];
  entities?: ApiEntityList;
  // Statistics for the current filter (fetched off the auto-refresh timer).
  statistics?: ApiEverestInvoiceStatistics;
}

class EverestInvoiceList extends Component<IEverestInvoiceListProps, IEverestInvoiceListState> {
  // Shared across all rows so org id -> name lookups are cached/deduplicated.
  private orgCache = new OrganizationCache();

  state: IEverestInvoiceListState = {
    errorMessage: undefined,
    invoices: undefined,
    downloading: false,
    // Default to open-only on the standalone invoices page; the org-embedded
    // view (organization_id set) keeps showing all of that org's invoices.
    stateFilter: this.props.organization_id ? StateFilter.All : StateFilter.Open,
    invoiceNumber: "",
    page: 0,
    pageSize: 10,
    orgId: "",
    orgText: "",
    orgOptions: [],
    orgSearchLoading: false,
    invoiceDateFromStr: "",
    invoiceDateToStr: "",
    datePresetID: "all",
    tierID: "",
    entityID: "",
  };

  // Parse the from/to date strings into inclusive UTC bounds: start-of-day for
  // "from", end-of-day for "to" (invoice_date is stored at midnight UTC, so
  // end-of-day keeps a same-day invoice included). An empty string = no bound.
  invoiceDateRange = (): { from?: Date; to?: Date } => {
    const from = this.state.invoiceDateFromStr ? moment.utc(this.state.invoiceDateFromStr, "YYYY-MM-DD").startOf("day").toDate() : undefined;
    const to = this.state.invoiceDateToStr ? moment.utc(this.state.invoiceDateToStr, "YYYY-MM-DD").endOf("day").toDate() : undefined;
    return { from, to };
  };

  reloadInvoices = async () => {
    const req: ApiListEverestInvoicesRequest = {
      options: { page: this.state.page, page_size: this.state.pageSize },
      ...stateFilterOf(this.state.stateFilter).request,
    };
    if (!!this.props.organization_id) {
      req.organization_id = this.props.organization_id;
    }
    if (!!this.state.orgId) {
      req.organization_id = this.state.orgId;
    }
    if (this.state.invoiceNumber) {
      req.invoice_number = this.state.invoiceNumber;
    }
    const { from, to } = this.invoiceDateRange();
    if (from) req.invoice_date_from = from;
    if (to) req.invoice_date_to = to;
    if (this.state.tierID) req.organization_tier_id = this.state.tierID;
    if (this.state.entityID) req.entity_id = this.state.entityID;
    const invoices = await apiClients.idashboardClient.ListEverestInvoices(req);
    this.setState({ invoices });
  };

  refreshInvoices = () => {
    this.props.refreshNow && this.props.refreshNow(this.reloadInvoices);
  };

  reloadStatistics = async () => {
    const req: ApiListEverestInvoicesRequest = {
      ...stateFilterOf(this.state.stateFilter).request,
    };
    if (!!this.props.organization_id) req.organization_id = this.props.organization_id;
    if (!!this.state.orgId) req.organization_id = this.state.orgId;
    if (this.state.invoiceNumber) {
      req.invoice_number = this.state.invoiceNumber;
    }
    const { from, to } = this.invoiceDateRange();
    if (from) req.invoice_date_from = from;
    if (to) req.invoice_date_to = to;
    if (this.state.tierID) req.organization_tier_id = this.state.tierID;
    if (this.state.entityID) req.entity_id = this.state.entityID;
    const statistics = await apiClients.idashboardClient.GetEverestInvoiceStatistics(req);
    this.setState({ statistics });
  };

  refreshStatistics = () => {
    this.props.refreshNow && this.props.refreshNow(this.reloadStatistics);
  };

  componentDidMount() {
    this.props.refreshWithTimer && this.props.refreshWithTimer(this.reloadInvoices, 60000);
    this.refreshStatistics();
    if (!this.props.organization_id) {
      this.loadFilterOptions();
    }
  }

  // loadFilterOptions fetches the tier and entity lists backing the two
  // dropdowns; only needed on the standalone page (the org-embedded view hides them).
  loadFilterOptions = async () => {
    try {
      const [tiers, entities] = await Promise.all([apiClients.idashboardClient.ListTiers({}), apiClients.idashboardClient.ListEntities({})]);
      this.setState({ tiers: tiers.items, entities });
    } catch (e) {
      this.setState({ errorMessage: e });
    }
  };

  onDownloadInvoice = async (inv: ApiEverestInvoice) => {
    try {
      this.setState({ downloading: true, errorMessage: undefined });
      await DownloadEverestInvoice(inv.invoice_uuid || "", this.props.auth, inv.invoice_date, inv.invoice_number);
    } catch (e) {
      this.setState({ errorMessage: e });
    } finally {
      this.setState({ downloading: false });
    }
  };

  onNextPage = () => {
    this.setState((s) => ({ page: s.page + 1 }), this.refreshInvoices);
  };
  onPreviousPage = () => {
    this.setState((s) => ({ page: Math.max(0, s.page - 1) }), this.refreshInvoices);
  };
  onStateFilterChanged = (v: StateFilter) => {
    this.setState({ stateFilter: v, page: 0 }, () => {
      this.refreshInvoices();
      this.refreshStatistics();
    });
  };

  // Invoice-number search is a substring match, so debounce the reload while typing.
  reloadAllDebounced = _.debounce(() => {
    this.refreshInvoices();
    this.refreshStatistics();
  }, 300);

  onInvoiceNumberChanged = (v: string) => {
    this.setState({ invoiceNumber: v, page: 0 }, this.reloadAllDebounced);
  };

  // searchOrganizations queries organizations by partial name, and — when the
  // query is purely numeric — also by exact organization id, so the user can
  // filter by either. Results are merged (deduped by id) and labelled
  // "Name (id)" so both are visible. Debounced so typing does not spam the API.
  searchOrganizations = _.debounce(async (query: string) => {
    try {
      const nameReq: ApiListAllOrganizationsRequest = { name: query, name_like: true, options: { page: 0, page_size: 20 } };
      const requests = [apiClients.idashboardClient.ListOrganizations(nameReq)];
      if (/^\d+$/.test(query)) {
        const idReq: ApiListAllOrganizationsRequest = { organization_id: query, options: { page: 0, page_size: 5 } };
        requests.push(apiClients.idashboardClient.ListOrganizations(idReq));
      }
      const lists = await Promise.all(requests);
      const byId = new Map<string, IOrgOption>();
      lists.forEach((list) =>
        (list.items || []).forEach((o: ApiOrganization) => {
          const id = o.id || "";
          if (id && !byId.has(id)) {
            byId.set(id, { key: id, value: id, text: `${o.name || "(no name)"} (${id})` });
          }
        })
      );
      this.setState({ orgOptions: Array.from(byId.values()), orgSearchLoading: false });
    } catch (e) {
      this.setState({ errorMessage: e, orgSearchLoading: false });
    }
  }, 300);

  onOrgSearchChange = (query: string) => {
    const q = query.trim();
    if (q.length < 2) {
      // Below the minimum length we clear results rather than query for everything.
      this.searchOrganizations.cancel();
      this.setState({ orgOptions: [], orgSearchLoading: false });
      return;
    }
    this.setState({ orgSearchLoading: true });
    this.searchOrganizations(q);
  };

  onOrgSelected = (value: string) => {
    const selected = this.state.orgOptions.find((o) => o.value === value);
    this.setState({ orgId: value, orgText: selected ? selected.text : "", page: 0 }, () => {
      this.refreshInvoices();
      this.refreshStatistics();
    });
  };

  // applyDatePreset fills the from/to inputs from a quick-range preset ("all"
  // clears them). The inputs remain the source of truth, so manual edits below
  // just reset the preset selector to "custom".
  applyDatePreset = (id: string) => {
    if (id === "all") {
      this.setState({ datePresetID: "all", invoiceDateFromStr: "", invoiceDateToStr: "", page: 0 }, () => {
        this.refreshInvoices();
        this.refreshStatistics();
      });
      return;
    }
    const f = CreationDateFilters.find((x) => x.id === id);
    if (!f) return;
    const from = f.filterStart ? f.filterStart(momentNow().add(-f.created_from_hours, "hours")) : undefined;
    const to = f.filterEnd ? f.filterEnd(momentNow().add(-f.created_to_hours, "hours")) : undefined;
    this.setState(
      {
        datePresetID: id,
        invoiceDateFromStr: from ? from.format("YYYY-MM-DD") : "",
        invoiceDateToStr: to ? to.format("YYYY-MM-DD") : "",
        page: 0,
      },
      () => {
        this.refreshInvoices();
        this.refreshStatistics();
      }
    );
  };

  onTierChanged = (v: string) => {
    this.setState({ tierID: v, page: 0 }, () => {
      this.refreshInvoices();
      this.refreshStatistics();
    });
  };
  onEntityChanged = (v: string) => {
    this.setState({ entityID: v, page: 0 }, () => {
      this.refreshInvoices();
      this.refreshStatistics();
    });
  };

  onDateFromChanged = (v: string) => {
    this.setState({ invoiceDateFromStr: v, datePresetID: "", page: 0 }, () => {
      this.refreshInvoices();
      this.refreshStatistics();
    });
  };
  onDateToChanged = (v: string) => {
    this.setState({ invoiceDateToStr: v, datePresetID: "", page: 0 }, () => {
      this.refreshInvoices();
      this.refreshStatistics();
    });
  };

  handleDismissError = () => {
    this.setState({ errorMessage: undefined });
  };

  // filterContextText summarises the filters the statistics reflect, shown in the
  // panel header so the numbers are never read out of context.
  filterContextText = () => {
    const parts: string[] = [];
    if (this.state.orgText) {
      parts.push(this.state.orgText);
    }
    if (this.state.invoiceNumber) {
      parts.push(`No. "${this.state.invoiceNumber}"`);
    }
    if (this.state.tierID) {
      const tier = (this.state.tiers || []).find((t) => t.id === this.state.tierID);
      parts.push(`Tier: ${(tier && tier.name) || this.state.tierID}`);
    }
    if (this.state.entityID) {
      const entity = ((this.state.entities || {}).items || []).find((e) => e.id === this.state.entityID);
      parts.push(`Entity: ${(entity && entity.name) || this.state.entityID}`);
    }
    parts.push(this.state.stateFilter === StateFilter.All ? "All states" : stateFilterOf(this.state.stateFilter).label);
    parts.push(
      this.state.invoiceDateFromStr || this.state.invoiceDateToStr
        ? `${this.state.invoiceDateFromStr || "…"} – ${this.state.invoiceDateToStr || "…"}`
        : "All dates"
    );
    return (
      <>
        Filter:{" "}
        {parts.map((p, i) => (
          <React.Fragment key={i}>
            {i > 0 ? " · " : ""}
            <b>{p}</b>
          </React.Fragment>
        ))}
      </>
    );
  };

  render() {
    const items = (this.state.invoices || {}).items || [];
    const stats = this.state.statistics;
    // Keep the currently selected organization present in the option list so the
    // Dropdown still shows its label after a later search replaces the results.
    const orgOptions: IOrgOption[] = [...this.state.orgOptions];
    if (this.state.orgId && !orgOptions.some((o) => o.value === this.state.orgId)) {
      orgOptions.unshift({ key: this.state.orgId, value: this.state.orgId, text: this.state.orgText || this.state.orgId });
    }
    // The Dropdowns use the "all" sentinel for "no filter" (an empty value renders as
    // an unselected control); the state keeps "" so the request builders stay simple.
    const tierOptions = [
      { key: "tier-all", text: "Tier: all", value: "all" },
      ..._.orderBy(this.state.tiers || [], "name").map((t) => ({ key: `tier-${t.id || ""}`, text: `Tier: ${t.name || ""}`, value: t.id || "" })),
    ];
    const entityOptions = [
      { key: "entity-all", text: "Entity: all", value: "all" },
      ...((this.state.entities || {}).items || []).map((e) => ({ key: `entity-${e.id || ""}`, text: `Entity: ${e.name || ""}`, value: e.id || "" })),
    ];
    const fromSentinel = (v: string) => (v === "all" ? "" : v);
    return (
      <ContentSegment>
        <ErrorMessage active={!!this.state.errorMessage} onDismiss={this.handleDismissError} message={this.state.errorMessage} />
        <Processing active={this.state.downloading} message="Downloading, please wait..." />
        {this.props.showHeader && (
          <SecondaryMenu>
            <Menu.Item header>{this.props.headerTitle || "Invoices"}</Menu.Item>
          </SecondaryMenu>
        )}
        <FilterMenu borderless pointing stackable>
          <Menu.Item>
            <Input icon="search" placeholder="Invoice number" value={this.state.invoiceNumber} onChange={(_, d) => this.onInvoiceNumberChanged(d.value)} />
          </Menu.Item>
          {!this.props.organization_id && (
            <Menu.Item>
              <Dropdown
                selection
                search
                clearable
                icon="search"
                minCharacters={2}
                placeholder="Org Name or ID"
                value={this.state.orgId}
                options={orgOptions}
                loading={this.state.orgSearchLoading}
                noResultsMessage={this.state.orgSearchLoading ? "Searching..." : "No matching organizations"}
                onSearchChange={(_, d) => this.onOrgSearchChange((d.searchQuery as string) || "")}
                onChange={(_, d) => this.onOrgSelected((d.value as string) || "")}
              />
            </Menu.Item>
          )}
          {!this.props.organization_id && (
            <Menu.Item>
              <Dropdown
                selection
                value={this.state.tierID || "all"}
                options={tierOptions}
                onChange={(_, d) => this.onTierChanged(fromSentinel(d.value as string))}
              />
            </Menu.Item>
          )}
          {!this.props.organization_id && (
            <Menu.Item>
              <Dropdown
                selection
                value={this.state.entityID || "all"}
                options={entityOptions}
                onChange={(_, d) => this.onEntityChanged(fromSentinel(d.value as string))}
              />
            </Menu.Item>
          )}
          <Menu.Item>
            <Dropdown
              selection
              value={this.state.stateFilter}
              options={stateFilters.map((f) => ({ key: f.value, text: f.label, value: f.value }))}
              onChange={(_, d) => this.onStateFilterChanged(d.value as StateFilter)}
            />
          </Menu.Item>
          <Menu.Item>
            <Dropdown
              selection
              placeholder="Custom range"
              value={this.state.datePresetID}
              options={[
                { key: "crd-all", text: "Invoice date: all", value: "all" },
                ...CreationDateFilters.map((f: CreationDateFilter) => ({ key: `crd-${f.id}`, text: `Invoice date: ${f.name}`, value: f.id })),
              ]}
              onChange={(_, d) => this.applyDatePreset(d.value as string)}
            />
          </Menu.Item>
          <Menu.Item>
            <Input
              size="mini"
              label="Invoice date from"
              type="date"
              value={this.state.invoiceDateFromStr}
              onChange={(_, d) => this.onDateFromChanged(d.value)}
            />
          </Menu.Item>
          <Menu.Item>
            <Input size="mini" label="to" type="date" value={this.state.invoiceDateToStr} onChange={(_, d) => this.onDateToChanged(d.value)} />
          </Menu.Item>
          <Menu.Item position="right" fitted="vertically">
            <PagingButtons
              size="tiny"
              page={this.state.page}
              pageSize={this.state.pageSize}
              count={items.length}
              onNextPage={this.onNextPage}
              onPreviousPage={this.onPreviousPage}
            />
          </Menu.Item>
        </FilterMenu>
        {stats && (
          <StatsCard>
            <div className="split">
              <div className="cell head left">
                <div className="hrow">
                  <span className="title">Summary</span>
                  <span className="ctx">{this.filterContextText()}</span>
                </div>
              </div>
              <div className="cell head right">
                <div className="title">Amounts by currency</div>
              </div>

              <div className="cell body left">
                <div className="kpis">
                  <div className="kpi kpi--total">
                    <span className="kpi__value">{stats.total_invoices || 0}</span>
                    <span className="kpi__label">Invoices</span>
                  </div>
                  <div className="kpi">
                    <span className="kpi__value">{stats.unpaid_invoices || 0}</span>
                    <span className="kpi__label">
                      <span className="dot" />
                      Unpaid
                    </span>
                  </div>
                  <div className="kpi">
                    <span className="kpi__value">{stats.partially_paid_invoices || 0}</span>
                    <span className="kpi__label">
                      <span className="dot" />
                      Partial
                    </span>
                  </div>
                  <div className="kpi kpi--good">
                    <span className="kpi__value">{stats.paid_invoices || 0}</span>
                    <span className="kpi__label">
                      <span className="dot" />
                      Paid
                    </span>
                  </div>
                  <div className="kpi kpi--good">
                    <span className="kpi__value">{stats.credited_invoices || 0}</span>
                    <span className="kpi__label">
                      <span className="dot" />
                      Credited
                    </span>
                  </div>
                  <div className="kpi kpi--overdue">
                    <span className="kpi__value">{stats.overdue_invoices || 0}</span>
                    <span className="kpi__label">
                      <span className="dot" />
                      Overdue
                    </span>
                  </div>
                </div>
              </div>

              <div className="cell body right">
                {(stats.currency_values || []).length > 0 ? (
                  <div className="currencies">
                    {(stats.currency_values || []).map((cv) => {
                      const dueZero = !amountIsPositive(cv.total_amount_due);
                      return (
                        <div className="cur" key={cv.currency}>
                          <span className="cur-code">{cv.currency}</span>
                          <div className="cur-tbl">
                            <span className="h">Total</span>
                            <span className="h">Due</span>
                            <span className="v">{groupThousands(cv.total_amount && cv.total_amount.amount)}</span>
                            <span className={`v due${dueZero ? " zero" : ""}`}>{groupThousands(cv.total_amount_due && cv.total_amount_due.amount)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="ctx">—</div>
                )}
              </div>
            </div>
          </StatsCard>
        )}
        <Table striped>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Number</Table.HeaderCell>
              <Table.HeaderCell>Organization</Table.HeaderCell>
              <Table.HeaderCell>Invoice date</Table.HeaderCell>
              <Table.HeaderCell>Due date</Table.HeaderCell>
              <Table.HeaderCell>Total</Table.HeaderCell>
              <Table.HeaderCell>Amount due</Table.HeaderCell>
              <Table.HeaderCell>State</Table.HeaderCell>
              <Table.HeaderCell>Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {items.map((item) => (
              <RowView
                key={item.invoice_uuid}
                item={item}
                orgCache={this.orgCache}
                onInvoiceSelected={this.props.onInvoiceSelected}
                onOrganizationSelected={this.props.onOrganizationSelected}
                onDownload={() => this.onDownloadInvoice(item)}
              />
            ))}
          </Table.Body>
        </Table>
      </ContentSegment>
    );
  }
}

export default withRefresh()(EverestInvoiceList);
